import { createContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

function rgbaToHsl(rgba: string): string | null {
  const match = rgba.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+)?\s*\)/i);
  if (!match) return null;
  
  const r = parseInt(match[1], 10) / 255;
  const g = parseInt(match[2], 10) / 255;
  const b = parseInt(match[3], 10) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const ThemeContext = createContext<null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { data: pageBackground } = useQuery<{ lightBg: string }>({
    queryKey: ["/api/settings/page-background"],
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!pageBackground) return;
    
    const root = document.documentElement;
    const hsl = rgbaToHsl(pageBackground.lightBg);
    
    if (hsl) {
      root.style.setProperty('--background', hsl);
    }
  }, [pageBackground]);

  return (
    <ThemeContext.Provider value={null}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return { theme: "light" as const };
}
